import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X, Send, Calendar } from "lucide-react";
import axios from "axios";
import { API, getVisitorId, trackEvent } from "@/lib/tracking";

const getChatSessionId = () => {
  if (typeof window === "undefined") return "s-" + Date.now();
  const existing = window.localStorage.getItem("ranger_chat_session_id");
  if (existing) return existing;
  const created = typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : "s-" + Date.now();
  window.localStorage.setItem("ranger_chat_session_id", created);
  return created;
};

const GREETING = {
  role: "assistant",
  content:
    "Hi! I'm the Ranger Assistant. I can answer questions about our paving and highway services, FOB asphalt sales, or help you book a quick appointment with our team. How can I help?",
};

const SUGGESTIONS = ["What services do you offer?", "Where are your asphalt plants?", "Book an appointment"];

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(getChatSessionId);
  const scrollRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const hydrateConversation = async () => {
      try {
        const { data } = await axios.get(`${API}/chat/${sessionId}`);
        if (!cancelled && data.messages?.length) setMessages(data.messages);
      } catch {
        // Keep the greeting if there is no saved conversation yet.
      }
    };
    hydrateConversation();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const toggle = () => {
    setOpen((o) => {
      if (!o) trackEvent("chat_open");
      return !o;
    });
  };

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || loading) return;
    const next = [...messages, { role: "user", content }];
    setMessages(next);
    setInput("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        visitor_id: getVisitorId(),
        messages: next.filter((m) => m.role !== "system"),
      });
      setMessages(data.messages?.length ? data.messages : [...next, { role: "assistant", content: data.reply }]);
      if (data.action?.type === "appointment_booked") trackEvent("appointment_booked", { label: data.action.id });
      if (data.action?.type === "lead_captured") trackEvent("lead_captured", { label: data.action.id });
    } catch {
      setMessages([
        ...next,
        { role: "assistant", content: "Sorry, I hit a snag. Please call 561.793.9400 or use the contact form." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        data-testid="chatbot-toggle-button"
        onClick={toggle}
        className="fixed bottom-6 right-6 z-50 bg-rc-red text-white w-15 h-15 rounded-full shadow-2xl flex items-center justify-center hover:bg-rc-reddark transition-colors"
        style={{ width: 60, height: 60 }}
        aria-label="Open chat"
      >
        <AnimatePresence mode="wait">
          {open ? (
            <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}>
              <X size={24} />
            </motion.span>
          ) : (
            <motion.span key="m" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }}>
              <MessageSquare size={24} />
            </motion.span>
          )}
        </AnimatePresence>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chatbot-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-6 z-50 w-[calc(100vw-3rem)] sm:w-[380px] bg-white shadow-2xl border border-black/10 overflow-hidden flex flex-col"
            style={{ height: 520, maxHeight: "70vh" }}
          >
            <div className="bg-rc-charcoal text-white px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 bg-rc-red flex items-center justify-center font-heading font-black text-sm">R</div>
                <div>
                  <div className="font-heading font-bold leading-tight">Ranger Assistant</div>
                  <div className="text-white/50 text-[11px] font-mono">Typically replies instantly</div>
                </div>
              </div>
              <button data-testid="chatbot-close" onClick={() => setOpen(false)} className="text-white/70 hover:text-white">
                <X size={18} />
              </button>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto rc-chat-scroll p-4 bg-rc-bg flex flex-col gap-3">
              {messages.map((m, i) => (
                <div
                  key={i}
                  data-testid={`chat-msg-${m.role}`}
                  className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "self-end bg-rc-red text-white"
                      : "self-start bg-white border border-black/10 text-rc-charcoal"
                  }`}
                >
                  {m.content}
                </div>
              ))}
              {loading && (
                <div className="self-start bg-white border border-black/10 px-4 py-3 flex gap-1">
                  {[0, 1, 2].map((d) => (
                    <span
                      key={d}
                      className="w-1.5 h-1.5 bg-rc-brown rounded-full animate-bounce"
                      style={{ animationDelay: `${d * 0.15}s` }}
                    />
                  ))}
                </div>
              )}
              {messages.length === 1 && !loading && (
                <div className="flex flex-wrap gap-2 mt-1">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      data-testid={`chat-suggestion`}
                      onClick={() => send(s)}
                      className="text-xs border border-rc-red/40 text-rc-red px-3 py-1.5 hover:bg-rc-red hover:text-white transition-colors flex items-center gap-1"
                    >
                      {s.includes("appointment") && <Calendar size={12} />}
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="p-3 border-t border-black/10 bg-white flex gap-2"
            >
              <input
                data-testid="chatbot-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type a message…"
                className="flex-1 px-3 py-2.5 text-sm border border-black/15 outline-none focus:border-rc-red"
              />
              <button
                data-testid="chatbot-send-button"
                type="submit"
                disabled={loading}
                className="bg-rc-red text-white px-3.5 flex items-center justify-center hover:bg-rc-reddark transition-colors disabled:opacity-60"
              >
                <Send size={16} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
